// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * Mention Parser Utility
 *
 * Extracts @mentions from text content for discussion tagging.
 */

/**
 * Parsed mention from content
 */
export interface ParsedMention {
  /** The handle without the @ symbol */
  identifier: string;
  /** Character position where the @ starts */
  position: number;
}

/**
 * Regex to match @mentions
 * Supports: letters (case-insensitive), numbers, hyphens, underscores
 * Identifiers are normalized to lowercase in the output
 * Examples: @viktor-petrov, @team_xyz789, @CEO
 */
const MENTION_REGEX = /@([a-zA-Z0-9_-]+)/g;

/**
 * Extract @mentions from content string
 *
 * @param content - The text content to parse
 * @returns Array of parsed mentions with identifier and position
 *
 * @example
 * ```typescript
 * parseMentions('Hey @viktor-petrov check this');
 * // Returns: [{ identifier: 'viktor-petrov', position: 4 }]
 * ```
 */
export function parseMentions(content: string): ParsedMention[] {
  if (!content) {
    return [];
  }

  const mentions: ParsedMention[] = [];
  let match: RegExpExecArray | null;

  // Reset regex state for fresh matching
  MENTION_REGEX.lastIndex = 0;

  while ((match = MENTION_REGEX.exec(content)) !== null) {
    mentions.push({
      identifier: match[1].toLowerCase(),
      position: match.index,
    });
  }

  return mentions;
}
